import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    TextField,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress
} from '@mui/material';
import {
    Search as SearchIcon,
    Send as SendIcon,
    AttachFile as AttachFileIcon,
    Add as AddIcon
} from '@mui/icons-material';
import DashboardLayout from '../../components/DashboardLayout';
import ChatItem from '../../components/ChatItem';
import MediaPreview from '../../components/MediaPreview';
import api from '../../utils/api';
import { getContacts } from '../../services/contactService';

const Chats = () => {
    // Объединенное состояние для чатов
    const [chatsData, setChatsData] = useState({
        contactsWithChats: [],
        flattenedChats: [],
        availableAccounts: [],
        loading: true,
        newMessageNotification: {},
        hasMoreChats: true,
        loadingMoreChats: false,
        oldestChatCursor: null
    });
    
    // State for incremental limit loading
    const [currentLimit, setCurrentLimit] = useState(100);
    const currentLimitRef = useRef(100);

    const [searchTerm, setSearchTerm] = useState('');
    const [accountFilter, setAccountFilter] = useState('');

    // State for create chat dialog
    const [createChatOpen, setCreateChatOpen] = useState(false);
    const [selectedInstance, setSelectedInstance] = useState('');
    const [selectedContact, setSelectedContact] = useState('');
    const [contacts, setContacts] = useState([]);
    const [instances, setInstances] = useState([]);
    const [contactsLoading, setContactsLoading] = useState(false);
    const [createChatLoading, setCreateChatLoading] = useState(false);

    // Refs для стабильных ссылок
    const selectedChatRef = useRef(null);
    const contactsWithChatsRef = useRef([]);
    const pollingIntervalRef = useRef(null);

    // State for selected chat and messages
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [oldestCursor, setOldestCursor] = useState(null);
    const [newestCursor, setNewestCursor] = useState(null);
    const [newMessage, setNewMessage] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [uploadingFile, setUploadingFile] = useState(false);

    // Мемоизируем функцию преобразования данных
    const flattenContactsToChats = useCallback((contactsData) => {
        const flattened = [];

        contactsData.forEach(contact => {
            Object.entries(contact.accounts).forEach(([accountPhone, accountData]) => {
                flattened.push({
                    id: `${contact.contact.id}_${accountPhone}`,
                    contact_id: contact.contact.id,
                    account_phone: accountPhone,
                    chat_id: accountData.chat_id,
                    contact: contact.contact,
                    account: accountData,
                    unread_count: accountData.unread_count,
                    last_message_at: accountData.last_message_at
                });
            });
        });

        return flattened.sort((a, b) =>
            new Date(b.last_message_at) - new Date(a.last_message_at)
        );
    }, []);

    // Мемоизируем отфильтрованные чаты
    const filteredChats = useMemo(() => {
        let result = chatsData.flattenedChats;

        if (searchTerm) {
            result = result.filter(chat =>
                chat.contact?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                chat.contact?.phone?.includes(searchTerm)
            );
        }

        if (accountFilter) {
            result = result.filter(chat => chat.account_phone === accountFilter);
        }

        return result;
    }, [chatsData.flattenedChats, searchTerm, accountFilter]);

    // Оптимизированная функция загрузки данных с пагинацией
    const fetchChatsData = useCallback(async (isLoadMore = false) => {
        try {
            let newLimit = currentLimitRef.current;
            
            if (isLoadMore) {
                setChatsData(prev => ({ ...prev, loadingMoreChats: true }));
                newLimit = currentLimitRef.current + 100;
                setCurrentLimit(newLimit);
                currentLimitRef.current = newLimit; // Update ref immediately
            }

            const params = {
                limit: newLimit
            };

            const chatsResponse = await api.get('/chats', { params });
            const { data: contactsData, pagination } = chatsResponse.data;
            
            const flattened = flattenContactsToChats(contactsData);

            const accountsSet = new Set();
            contactsData.forEach(contact => {
                Object.keys(contact.accounts).forEach(phone => {
                    accountsSet.add(phone);
                });
            });

            setChatsData(prev => ({
                ...prev,
                contactsWithChats: contactsData,
                flattenedChats: flattened,
                availableAccounts: Array.from(accountsSet),
                loading: false,
                loadingMoreChats: false,
                hasMoreChats: pagination.hasMore,
                oldestChatCursor: pagination.oldestCursor
            }));

            contactsWithChatsRef.current = contactsData;

        } catch (error) {
            console.error('Error fetching chats:', error);
            setChatsData(prev => ({ 
                ...prev, 
                loading: false, 
                loadingMoreChats: false 
            }));
        }
    }, [flattenContactsToChats]);

    // Оптимизированный поллинг с проверкой видимости страницы
    const startPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }

        pollingIntervalRef.current = setInterval(async () => {
            if (document.hidden) return;

            try {
                // Use current limit from ref to preserve expanded chat list
                const chatsResponse = await api.get('/chats', {
                    params: { limit: currentLimitRef.current }
                });
                const { data: contactsData } = chatsResponse.data;
                const hasChanges = JSON.stringify(contactsWithChatsRef.current) !==
                    JSON.stringify(contactsData);

                if (!hasChanges) return;

                const newNotifications = {};
                contactsData.forEach(contact => {
                    Object.entries(contact.accounts).forEach(([accountPhone, accountData]) => {
                        const existingContact = contactsWithChatsRef.current.find(
                            c => c.contact.id === contact.contact.id
                        );
                        const existingAccount = existingContact?.accounts[accountPhone];

                        if (existingAccount && accountData.unread_count > existingAccount.unread_count) {
                            const notificationKey = `${contact.contact.id}_${accountPhone}`;
                            newNotifications[notificationKey] = true;
                        }
                    });
                });

                if (Object.keys(newNotifications).length > 0) {
                    setChatsData(prev => ({
                        ...prev,
                        newMessageNotification: {
                            ...prev.newMessageNotification,
                            ...newNotifications
                        }
                    }));

                    setTimeout(() => {
                        setChatsData(prev => {
                            const updated = { ...prev.newMessageNotification };
                            Object.keys(newNotifications).forEach(id => {
                                delete updated[id];
                            });
                            return {
                                ...prev,
                                newMessageNotification: updated
                            };
                        });
                    }, 3000);
                }

                const currentSelectedChat = selectedChatRef.current;
                if (currentSelectedChat) {
                    const updatedContact = contactsData.find(
                        c => c.contact.id === currentSelectedChat.contact_id
                    );
                    const updatedAccount = updatedContact?.accounts[currentSelectedChat.account_phone];

                    if (updatedAccount && updatedAccount.unread_count > 0) {
                        // Use optimized polling with lastMessageDate to fetch only new messages
                        const messagesResponse = await api.get(
                            `/chats/${updatedAccount.chat_id}/messages`,
                            {
                                params: {
                                    lastMessageDate: newestCursor,
                                    limit: 50,
                                    direction: 'ASC' // Get newest messages first
                                }
                            }
                        );

                        const { data: newMessages } = messagesResponse.data;
                        
                        // Only update if there are actually new messages
                        if (newMessages && newMessages.length > 0) {
                            // Filter out duplicates by checking if message with same id or external_id already exists
                            setMessages(prev => {
                                const existingIds = new Set(prev.map(m => m.id));
                                const existingExternalIds = new Set(prev.filter(m => m.external_id).map(m => m.external_id));
                                
                                const uniqueNewMessages = newMessages.filter(msg => {
                                    // Skip if message with same id exists
                                    if (existingIds.has(msg.id)) {
                                        return false;
                                    }
                                    // Skip if message with same external_id exists
                                    if (msg.external_id && existingExternalIds.has(msg.external_id)) {
                                        return false;
                                    }
                                    return true;
                                });
                                
                                if (uniqueNewMessages.length === 0) {
                                    return prev; // No new unique messages
                                }
                                
                                // Add new messages to the end (bottom) and sort by sent_at ASC
                                const combined = [...prev, ...uniqueNewMessages];
                                combined.sort((a, b) => {
                                    const dateA = new Date(a.sent_at);
                                    const dateB = new Date(b.sent_at);
                                    return dateA - dateB;
                                });
                                
                                // Auto-scroll to bottom when new messages arrive
                                setTimeout(() => {
                                    const messagesContainer = document.querySelector('[data-messages-container]');
                                    if (messagesContainer) {
                                        messagesContainer.scrollTop = messagesContainer.scrollHeight;
                                    }
                                }, 100);
                                
                                return combined;
                            });
                            
                            // Update newest cursor
                            const latestMessage = newMessages[newMessages.length - 1];
                            setNewestCursor(latestMessage.sent_at);
                        }

                        try {
                            await api.post(`/messages/read/${updatedAccount.chat_id}`);
                        } catch (error) {
                            console.error('Error marking messages as read:', error);
                        }
                    }
                }

                const flattened = flattenContactsToChats(contactsData);
                setChatsData(prev => ({
                    ...prev,
                    contactsWithChats: contactsData,
                    flattenedChats: flattened
                }));

                contactsWithChatsRef.current = contactsData;

            } catch (error) {
                console.error('Error polling chats:', error);
            }
        }, 5000);
    }, [flattenContactsToChats]);

    // Функция для загрузки файла на сервер
    const uploadFile = useCallback(async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await api.post('/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            return response.data.url; // URL файла на сервере
        } catch (error) {
            console.error('Error uploading file:', error);
            throw new Error('Failed to upload file');
        }
    }, []);

    // Инициализация
    useEffect(() => {
        const fetchInitialData = async () => {
            await fetchChatsData();

            try {
                const instancesResponse = await api.get('/instances');
                setInstances(instancesResponse.data);
            } catch (error) {
                console.error('Error fetching instances:', error);
            }
            
            // Start polling only after initial data is loaded
            startPolling();
        };

        fetchInitialData();

        const handleVisibilityChange = () => {
            if (document.hidden) {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                }
            } else {
                startPolling();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchChatsData, startPolling]);

    useEffect(() => {
        selectedChatRef.current = selectedChat;
    }, [selectedChat]);

    const handleChatSelect = useCallback((chat) => {
        setSelectedChat(chat);
    }, []);

    // Enhanced message loading with pagination
    // Load last 10 messages when opening chat, sorted ASC (oldest first, newest last)
    const fetchMessages = useCallback(async (chatId, isInitial = true) => {
        try {
            if (isInitial) {
                setMessagesLoading(true);
                setMessages([]);
                setHasMoreMessages(false);
                setOldestCursor(null);
                setNewestCursor(null);
            }

            // Load last 10 messages, sorted ASC (oldest first, newest last)
            const response = await api.get(`/chats/${chatId}/messages`, {
                params: {
                    limit: 10,
                    direction: 'DESC' // Get newest first from API
                }
            });

            const { data: messageData, pagination } = response.data;
            
            if (isInitial) {
                // Reverse to get ASC order (oldest first, newest last)
                const sortedMessages = [...messageData].reverse();
                setMessages(sortedMessages);
                setMessagesLoading(false);
                
                // Auto-scroll to bottom after messages load
                setTimeout(() => {
                    const messagesContainer = document.querySelector('[data-messages-container]');
                    if (messagesContainer) {
                        messagesContainer.scrollTop = messagesContainer.scrollHeight;
                    }
                }, 100);
            }

            setHasMoreMessages(pagination.hasMore);
            setOldestCursor(pagination.oldestCursor);
            setNewestCursor(pagination.newestCursor);

            // Mark messages as read
            try {
                await api.post(`/messages/read/${chatId}`);
                setTimeout(fetchChatsData, 500);
            } catch (error) {
                console.error('Error marking messages as read:', error);
            }

        } catch (error) {
            console.error('Error fetching messages:', error);
            if (isInitial) {
                setMessagesLoading(false);
            }
        }
    }, [fetchChatsData]);

    // Load more older messages when scrolling up
    const loadMoreMessages = useCallback(async () => {
        if (!selectedChat || !hasMoreMessages || loadingMore || !oldestCursor) return;

        try {
            setLoadingMore(true);

            // Save current scroll position and height
            const messagesContainer = document.querySelector('[data-messages-container]');
            const scrollHeightBefore = messagesContainer?.scrollHeight || 0;
            const scrollTopBefore = messagesContainer?.scrollTop || 0;

            const response = await api.get(`/chats/${selectedChat.chat_id}/messages`, {
                params: {
                    limit: 20,
                    direction: 'DESC',
                    cursor: oldestCursor
                }
            });

            const { data: messageData, pagination } = response.data;

            // Reverse to get ASC order and prepend to existing messages (older messages go to top)
            const sortedOlderMessages = [...messageData].reverse();
            setMessages(prev => [...sortedOlderMessages, ...prev]);
            setHasMoreMessages(pagination.hasMore);
            setOldestCursor(pagination.oldestCursor);

            // Restore scroll position after new messages are added
            setTimeout(() => {
                if (messagesContainer) {
                    const scrollHeightAfter = messagesContainer.scrollHeight;
                    const heightDifference = scrollHeightAfter - scrollHeightBefore;
                    messagesContainer.scrollTop = scrollTopBefore + heightDifference;
                }
            }, 50);

        } catch (error) {
            console.error('Error loading more messages:', error);
        } finally {
            setLoadingMore(false);
        }
    }, [selectedChat, hasMoreMessages, loadingMore, oldestCursor]);

    useEffect(() => {
        if (selectedChat) {
            fetchMessages(selectedChat.chat_id, true);
        }
    }, [selectedChat, fetchMessages]);

    useEffect(() => {
        if (selectedInstance && createChatOpen) {
            const fetchContactsData = async () => {
                try {
                    setContactsLoading(true);
                    const response = await getContacts({ instance_id: selectedInstance });
                    setContacts(response.data || []);
                } catch (error) {
                    console.error('Error fetching contacts:', error);
                    setContacts([]);
                } finally {
                    setContactsLoading(false);
                }
            };
            fetchContactsData();
        }
    }, [selectedInstance, createChatOpen]);

    const handleCreateChat = useCallback(async () => {
        if (!selectedInstance || !selectedContact) return;

        try {
            setCreateChatLoading(true);
            await api.post('/chats', {
                instance_id: selectedInstance,
                contact_id: selectedContact
            });

            await fetchChatsData();

            setCreateChatOpen(false);
            setSelectedInstance('');
            setSelectedContact('');
            setContacts([]);
        } catch (error) {
            console.error('Error creating chat:', error);
        } finally {
            setCreateChatLoading(false);
        }
    }, [selectedInstance, selectedContact, fetchChatsData]);

    // Исправленная функция отправки сообщения
    const handleSendMessage = useCallback(async () => {
        if (!newMessage.trim() && !attachment) return;
        if (!selectedChat?.account?.active_instance) return;

        let serverFileUrl = null;
        let messageType = 'text';

        // Если есть файл - загружаем его на сервер
        if (attachment) {
            try {
                setUploadingFile(true);
                serverFileUrl = await uploadFile(attachment.file);
                messageType = attachment.type;
                console.log('File uploaded successfully:', serverFileUrl); // Для отладки
            } catch (error) {
                console.error('Error uploading file:', error);
                alert('Failed to upload file. Please try again.');
                setUploadingFile(false);
                return;
            }
            setUploadingFile(false);
        }

        // Генерируем уникальный временный ID
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Создаем временное сообщение для отображения
        const tempMessage = {
            id: tempId,
            content: newMessage || '',
            direction: 'outgoing',
            sent_at: new Date().toISOString(),
            status: 'sending',
            type: messageType,
            file_url: serverFileUrl, // Используем реальный URL сервера для превью
            isTemporary: true
        };

        // Добавляем временное сообщение в конец списка (внизу)
        setMessages(prev => {
            const updated = [...prev, tempMessage];
            // Auto-scroll to bottom after adding message
            setTimeout(() => {
                const messagesContainer = document.querySelector('[data-messages-container]');
                if (messagesContainer) {
                    messagesContainer.scrollTop = messagesContainer.scrollHeight;
                }
            }, 100);
            return updated;
        });
        const messageContent = newMessage;
        const messageAttachment = attachment;

        setNewMessage('');
        setAttachment(null);

        try {
            let response;
            if (messageAttachment && serverFileUrl) {
                response = await api.post('/messages/media', {
                    instance_id: selectedChat.account.active_instance.id,
                    chat_id: selectedChat.chat_id,
                    media_url: serverFileUrl, // Реальный URL с сервера
                    caption: messageContent,
                    media_type: messageAttachment.type
                });
            } else {
                response = await api.post('/messages', {
                    instance_id: selectedChat.account.active_instance.id,
                    chat_id: selectedChat.chat_id,
                    content: messageContent
                });
            }

            console.log('Message sent successfully:', response.data); // Для отладки

            // Заменяем временное сообщение на реальное и сортируем по времени
            setMessages(prev => {
                const updated = prev.map(msg =>
                    msg.id === tempId ? { ...response.data } : msg
                );
                // Sort by sent_at to ensure correct order
                updated.sort((a, b) => {
                    const dateA = new Date(a.sent_at);
                    const dateB = new Date(b.sent_at);
                    return dateA - dateB;
                });
                // Auto-scroll to bottom after message is sent
                setTimeout(() => {
                    const messagesContainer = document.querySelector('[data-messages-container]');
                    if (messagesContainer) {
                        messagesContainer.scrollTop = messagesContainer.scrollHeight;
                    }
                }, 100);
                return updated;
            });

            setTimeout(fetchChatsData, 1000);

        } catch (error) {
            console.error('Error sending message:', error);

            // Удаляем временное сообщение при ошибке
            setMessages(prev => prev.filter(msg => msg.id !== tempId));
            setNewMessage(messageContent);
            setAttachment(messageAttachment);

            // Показываем ошибку пользователю
            alert('Failed to send message. Please try again.');
        }
    }, [newMessage, attachment, selectedChat, fetchChatsData, uploadFile]);

    const handleAttachment = useCallback((event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (attachment?.url?.startsWith('blob:')) {
            URL.revokeObjectURL(attachment.url);
        }

        const fakeUrl = URL.createObjectURL(file);
        let type = 'document';
        if (file.type.startsWith('image/')) type = 'image';
        if (file.type.startsWith('video/')) type = 'video';
        if (file.type.startsWith('audio/')) type = 'audio';

        setAttachment({
            file,
            url: fakeUrl,
            type,
            name: file.name
        });
    }, [attachment]);

    useEffect(() => {
        return () => {
            if (attachment?.url?.startsWith('blob:')) {
                URL.revokeObjectURL(attachment.url);
            }
        };
    }, [attachment]);

    const formatTime = useCallback((timestamp) => {
        if (!timestamp) return '';
        try {
            const date = new Date(timestamp);
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            
            // Calculate difference in days
            const daysDiff = Math.floor((today - messageDate) / (1000 * 60 * 60 * 24));
            
            const timeString = date.toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: false 
            });
            
            if (daysDiff === 0) {
                // Today - show just time
                return `Today ${timeString}`;
            } else if (daysDiff === 1) {
                // Yesterday - show "Yesterday" with time
                return `Yesterday ${timeString}`;
            } else if (daysDiff < 7) {
                // This week - show day name with time
                const dayName = date.toLocaleDateString([], { weekday: 'short' });
                return `${dayName} ${timeString}`;
            } else {
                // Older messages - show full date with time
                const dateString = date.toLocaleDateString([], {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                });
                return `${dateString} ${timeString}`;
            }
        } catch {
            return '';
        }
    }, []);

    const getStatusColor = useCallback((status) => {
        switch (status) {
            case 'authorized':
            case 'connected':
                return 'success';
            case 'deleted':
                return 'error';
            default:
                return 'warning';
        }
    }, []);

    return (
        <DashboardLayout>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Chats
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Manage your chats grouped by contacts and accounts
                </Typography>
            </Box>

            {/* Create Chat Dialog */}
            <Dialog open={createChatOpen} onClose={() => setCreateChatOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Create New Chat</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
                        <InputLabel>Select Instance</InputLabel>
                        <Select
                            value={selectedInstance}
                            onChange={(e) => setSelectedInstance(e.target.value)}
                            label="Select Instance"
                        >
                            {instances.map((instance) => (
                                <MenuItem key={instance.id} value={instance.id}>
                                    {instance.phone} ({instance.alias || 'No name'})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Select Contact</InputLabel>
                        <Select
                            value={selectedContact}
                            onChange={(e) => setSelectedContact(e.target.value)}
                            label="Select Contact"
                            disabled={!selectedInstance || contactsLoading}
                        >
                            {contacts.map((contact) => (
                                <MenuItem key={contact.id} value={contact.id}>
                                    {contact.name} ({contact.phone})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateChatOpen(false)}>Cancel</Button>
                    <Button
                        onClick={handleCreateChat}
                        disabled={!selectedInstance || !selectedContact || createChatLoading}
                    >
                        {createChatLoading ? <CircularProgress size={20} /> : 'Create Chat'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Grid container spacing={2} sx={{ height: 'calc(100vh - 180px)' }}>
                {/* Left sidebar - Chats list */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setCreateChatOpen(true)}
                                fullWidth
                                sx={{ mb: 2 }}
                            >
                                Create Chat
                            </Button>

                            <TextField
                                fullWidth
                                placeholder="Search contacts..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                size="small"
                                sx={{ mb: 2 }}
                            />

                            <FormControl fullWidth size="small">
                                <InputLabel>Filter by Account</InputLabel>
                                <Select
                                    value={accountFilter}
                                    onChange={(e) => setAccountFilter(e.target.value)}
                                    label="Filter by Account"
                                >
                                    <MenuItem value="">All Accounts</MenuItem>
                                    {chatsData.availableAccounts.map((phone) => {
                                        const inst = instances.find(i => String(i.phone) === String(phone));
                                        const name = inst?.alias || '';
                                        const label = name ? `${phone} (${name})` : String(phone);
                                        return (
                                            <MenuItem key={phone} value={phone}>
                                                {label}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ flexGrow: 1, overflow: 'auto', maxHeight: 'calc(60vh - 0px)' }}>
                            <List sx={{ py: 0 }}>
                                {chatsData.loading ? (
                                    <ListItem key="loading">
                                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 2 }}>
                                            <CircularProgress />
                                        </Box>
                                    </ListItem>
                                ) : filteredChats.length === 0 ? (
                                    <ListItem key="no-chats">
                                        <ListItemText
                                            primary="No chats found"
                                            secondary="Create a new chat to get started"
                                        />
                                    </ListItem>
                                ) : (
                                    <>
                                        {filteredChats.map((chat) => (
                                            <ChatItem
                                                key={chat.id}
                                                chat={chat}
                                                isSelected={selectedChat?.id === chat.id}
                                                onSelect={handleChatSelect}
                                                hasNewMessage={chatsData.newMessageNotification[chat.id]}
                                                formatTime={formatTime}
                                                getStatusColor={getStatusColor}
                                            />
                                        ))}
                                        
                                        {/* Load More Chats Button */}
                                        {chatsData.hasMoreChats && !searchTerm && !accountFilter && (
                                            <ListItem key="load-more-chats">
                                                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', py: 1 }}>
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() => fetchChatsData(true)}
                                                        disabled={chatsData.loadingMoreChats}
                                                        startIcon={chatsData.loadingMoreChats ? <CircularProgress size={16} /> : null}
                                                        size="small"
                                                    >
                                                        {chatsData.loadingMoreChats ? 'Loading...' : 'Load More Chats'}
                                                    </Button>
                                                </Box>
                                            </ListItem>
                                        )}
                                    </>
                                )}
                            </List>
                        </Box>
                    </Paper>
                </Grid>

                {/* Right side - Chat messages */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {selectedChat ? (
                            <>
                                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                    <Typography variant="h6">
                                        {selectedChat.contact?.name || 'Unknown Contact'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedChat.contact?.phone || 'No phone'} • {selectedChat.account_phone}
                                        {selectedChat.account.active_instance &&
                                            ` • ${selectedChat.account.active_instance.alias}`
                                        }
                                    </Typography>
                                </Box>

                                <Box 
                                    data-messages-container
                                    sx={{
                                        flexGrow: 1,
                                        overflow: 'auto',
                                        p: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        maxHeight: 'calc(100vh - 350px)'
                                    }}
                                    onScroll={(e) => {
                                        // Load more messages when scrolling to top
                                        const container = e.target;
                                        if (container.scrollTop === 0 && hasMoreMessages && !loadingMore) {
                                            loadMoreMessages();
                                        }
                                    }}
                                >
                                    {messages.length === 0 ? (
                                        <Typography key="no-messages" align="center" color="text.secondary">
                                            No messages yet
                                        </Typography>
                                    ) : (
                                        messages.map((message, index) => (
                                            <Box
                                                key={`message-${message.id || index}`}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: message.direction === 'outgoing' ? 'flex-end' : 'flex-start',
                                                    mb: 2
                                                }}
                                            >
                                                <Card
                                                    sx={{
                                                        maxWidth: '70%',
                                                        bgcolor: message.direction === 'outgoing' ? 'primary.light' : 'background.paper',
                                                        opacity: message.isTemporary ? 0.7 : 1
                                                    }}
                                                >
                                                    <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                                                        {message.type !== 'text' && message.file_url && (
                                                            <MediaPreview message={message} />
                                                        )}
                                                        {message.content && (
                                                            <Typography variant="body1">
                                                                {message.content}
                                                            </Typography>
                                                        )}
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}
                                                        >
                                                            {formatTime(message.sent_at)}
                                                            {message.direction === 'outgoing' && (
                                                                <span> • {message.status || 'sending'}</span>
                                                            )}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Box>
                                        ))
                                    )}
                                    
                                    {/* Load More Indicator at top */}
                                    {hasMoreMessages && messages.length > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1, pb: 2 }}>
                                            {loadingMore ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <Typography variant="caption" color="text.secondary">
                                                    Scroll up to load older messages
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>

                                <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                                    {attachment && (
                                        <Box sx={{
                                            mb: 1,
                                            p: 1,
                                            border: 1,
                                            borderColor: 'divider',
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}>
                                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                                {attachment.name}
                                            </Typography>
                                            <IconButton size="small" onClick={() => setAttachment(null)}>
                                                &times;
                                            </IconButton>
                                        </Box>
                                    )}
                                    <Box sx={{ display: 'flex' }}>
                                        <input
                                            type="file"
                                            id="attachment-input"
                                            style={{ display: 'none' }}
                                            onChange={handleAttachment}
                                        />
                                        <label htmlFor="attachment-input">
                                            <IconButton component="span" disabled={uploadingFile}>
                                                <AttachFileIcon />
                                            </IconButton>
                                        </label>
                                        <TextField
                                            fullWidth
                                            placeholder="Type a message..."
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                                            size="small"
                                            sx={{ mx: 1 }}
                                            disabled={uploadingFile}
                                        />
                                        <IconButton
                                            color="primary"
                                            onClick={handleSendMessage}
                                            disabled={uploadingFile}
                                        >
                                            {uploadingFile ? <CircularProgress size={20} /> : <SendIcon />}
                                        </IconButton>
                                    </Box>
                                </Box>
                            </>
                        ) : (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%'
                            }}>
                                <Typography variant="h6" color="text.secondary">
                                    Select a chat to start messaging
                                </Typography>
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </DashboardLayout>
    );
};

export default Chats;